import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import moment from "moment";
import { APIURL } from "../../../config";
import {
    Autocomplete, TextField, Box, IconButton, Typography, Button, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckIcon from "@mui/icons-material/Check";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import BlockIcon from "@mui/icons-material/Block";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FieldWorkerSVG from "../FieldWorkerSVG";
import { EMAR_STATUS, EMAR_LEGEND_ORDER, clientClock, adherenceColor, readApiError } from "../../../utils/emarStatus";

const MONTH_FMT = "YYYY-MM";
const CELL = 30;
const NAME_COL = 190;
const TIME_COL = 76;
const BORDER = "1px solid #dde3e8";

const SUMMARY_TILES = [
    { key: "administered", label: "Given", color: EMAR_STATUS.onTime.color },
    { key: "late", label: "Late", color: EMAR_STATUS.late.color },
    { key: "notGiven", label: "Not given", color: EMAR_STATUS.notGiven.color },
    { key: "missed", label: "Missed", color: EMAR_STATUS.missed.color },
    { key: "upcoming", label: "Upcoming", color: "#78909C" },
];

// SVG icons rather than text glyphs: a font positions ⊘ or · wherever its own
// metrics put them, so they never sit centred in the box.
const STATUS_ICONS = {
    onTime: { Icon: CheckIcon, scale: 0.8 },
    late: { Icon: PriorityHighIcon, scale: 0.75 },
    notGiven: { Icon: BlockIcon, scale: 0.72 },
    missed: { Icon: CloseIcon, scale: 0.8 },
    upcoming: { Icon: FiberManualRecordIcon, scale: 0.3 },
};

function StatusMark({ status, size = 20 }) {
    const style = EMAR_STATUS[status] || EMAR_STATUS.notDue;
    const mark = STATUS_ICONS[status];

    if (!mark) return null;

    return (
        <span
            style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle",
                width: size, height: size, borderRadius: 4, background: style.color, flexShrink: 0,
            }}
        >
            <mark.Icon style={{ fontSize: Math.round(size * mark.scale), color: style.text, display: "block" }} />
        </span>
    );
}

function Tile({ label, value, color }) {
    return (
        <Box sx={{ border: BORDER, borderRadius: 2, px: 2, py: 1, minWidth: 110, background: "#fff" }}>
            <Typography variant="caption" color="textSecondary">{label}</Typography>
            <Typography style={{ fontSize: "1.4rem", fontWeight: 700, color }}>{value ?? "—"}</Typography>
        </Box>
    );
}

/** What was recorded against one dose. Read-only: doses are charted from the client's Health tab. */
function CellDetailDialog({ detail, onClose }) {
    if (!detail) return null;

    const { medication, row, cell } = detail;
    const style = EMAR_STATUS[cell.status];
    const lines = [
        ["Scheduled", moment(cell.scheduledFor, "YYYY-MM-DD HH:mm:ss").format("ddd, MMM D, YYYY · h:mm A")],
        ["Dosage", medication.dosage],
        cell.takenAtDisplay && ["Given at", `${moment(cell.takenAt, "YYYY-MM-DD HH:mm:ss").format("MMM D, h:mm A")}${cell.minutesLate > 0 ? ` (${cell.minutesLate} min after scheduled)` : ""}`],
        cell.outcomeLabel && ["Outcome", cell.outcomeLabel],
        cell.reasonLabel && ["Reason", cell.reasonLabel],
        cell.notes && ["Note", cell.notes],
        cell.recordedAtDisplay && ["Recorded at", moment(cell.recordedAt, "YYYY-MM-DD HH:mm:ss").format("MMM D, h:mm A")],
        cell.markedByName && ["Recorded by", cell.markedByName],
        medication.condition && ["Condition", medication.condition],
        cell.occurrenceNumber && medication.maxOccurrences && ["Dose", `${cell.occurrenceNumber} of ${medication.maxOccurrences}`],
    ].filter(line => line && line[1]);

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <StatusMark status={cell.status} size={24} />
                <span>{medication.name} · {row.displayTime}</span>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" style={{ fontWeight: 600, color: style.color === "transparent" ? undefined : style.color, marginBottom: 8 }}>
                    {style.label}
                </Typography>
                {lines.map(([label, value]) => (
                    <Box key={label} sx={{ display: "flex", gap: 1, py: 0.5, borderBottom: "1px solid #f0f0f0" }}>
                        <Typography variant="body2" color="textSecondary" style={{ minWidth: 110 }}>{label}</Typography>
                        <Typography variant="body2">{value}</Typography>
                    </Box>
                ))}
                {cell.status === "missed" && (
                    <Typography variant="caption" color="textSecondary" component="div" style={{ marginTop: 10 }}>
                        Nothing was recorded for this dose. It can be charted from the client's Health tab.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Care Management > eMAR: the monthly electronic Medication Administration Record for
 * one client, as FWAPI GET /emar builds it — one row per medication x time of day,
 * one cell per day, coloured by what happened to that dose.
 */
export default function EMARReport(props) {
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [month, setMonth] = useState(moment().format(MONTH_FMT));
    const [chart, setChart] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [pdfAction, setPdfAction] = useState(null);
    const [error, setError] = useState(null);
    const [detail, setDetail] = useState(null);

    // The same list as the New Workflow form's client picker (WorkflowForm.js), re-read
    // when the selected program changes.
    useEffect(() => {
        axios
            .get(APIURL + "/customers/v2/associated", {
                params: {
                    company_id: props.user.comp_id,
                    employee_id: props.user.emp_id,
                    type: props.user.permissions.AdminLevelCustomers ? "Admin" : "Manager",
                    program_id: props.selectedProgramId ?? undefined,
                },
            })
            .then((response) => setCustomerList(response.data.filter((cus) => cus.cus_status === 'Active')))
            .catch((err) => console.log(err));
    }, [props.user, props.selectedProgramId]);

    // A client that dropped out of the list (program switched) is no longer selectable.
    useEffect(() => {
        if (selectedCustomer && !customerList.some((customer) => customer.cus_id === selectedCustomer.cus_id)) {
            setSelectedCustomer(null);
        }
    }, [customerList, selectedCustomer]);

    const requestParams = useCallback(() => ({
        cus_id: selectedCustomer?.cus_id,
        month,
        emp_id: props.user.emp_id,
        ...clientClock(),
    }), [selectedCustomer, month, props.user.emp_id]);

    useEffect(() => {
        if (!selectedCustomer) {
            setChart(null);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setError(null);

        axios
            .get(APIURL + "/emar", { params: requestParams() })
            .then((response) => { if (!cancelled) setChart(response.data); })
            .catch((err) => {
                console.log(err);
                if (!cancelled) {
                    setChart(null);
                    setError(readApiError(err, "Could not load the eMAR. Please try again."));
                }
            })
            .finally(() => { if (!cancelled) setIsLoading(false); });

        return () => { cancelled = true; };
    }, [selectedCustomer, requestParams]);

    const openPdf = async (action) => {
        setPdfAction(action);
        setError(null);
        try {
            const response = await axios.get(APIURL + "/emar/pdf", {
                params: requestParams(),
                responseType: "arraybuffer",
                headers: { Accept: "application/pdf" },
            });
            const fileURL = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));

            if (action === "download") {
                const link = document.createElement("a");
                link.href = fileURL;
                link.download = `eMAR_${(selectedCustomer.cus_name || "Client").replace(/[^A-Za-z0-9]+/g, "_")}_${month}.pdf`;
                link.click();
            } else {
                window.open(fileURL, "_blank");
            }
        } catch (err) {
            console.log(err);
            setError("Could not generate the PDF. Please try again.");
        }
        setPdfAction(null);
    };

    const shiftMonth = (delta) => setMonth(moment(month, MONTH_FMT).add(delta, "month").format(MONTH_FMT));

    const todayDay = moment().format(MONTH_FMT) === month ? moment().date() : null;
    const days = useMemo(() => Array.from({ length: chart?.daysInMonth || 0 }, (_, i) => i + 1), [chart]);
    const gridWidth = NAME_COL + TIME_COL + days.length * CELL;

    const stickyName = { position: "sticky", left: 0, zIndex: 2, background: "#fff", width: NAME_COL, minWidth: NAME_COL, maxWidth: NAME_COL };
    const stickyTime = { position: "sticky", left: NAME_COL, zIndex: 2, background: "#fff", width: TIME_COL, minWidth: TIME_COL, borderRight: BORDER };

    return (
        <Box sx={{ p: 1.5 }}>
            {/* Filters */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
                <Autocomplete
                    id="emar-client-select"
                    options={customerList}
                    value={selectedCustomer}
                    getOptionLabel={(option) => option.cus_name || ""}
                    isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
                    onChange={(event, value) => setSelectedCustomer(value)}
                    renderOption={(optionProps, option) => (
                        <li {...optionProps} key={option.cus_id}>
                            {option.cus_name}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Select Client" />}
                    sx={{ width: 280 }}
                    size="small"
                />

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <IconButton size="small" onClick={() => shiftMonth(-1)} aria-label="Previous month"><ChevronLeftIcon /></IconButton>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                            label="Month"
                            views={["year", "month"]}
                            openTo="month"
                            format="MMMM YYYY"
                            value={moment(month, MONTH_FMT)}
                            onChange={(value) => { if (value && value.isValid()) setMonth(value.format(MONTH_FMT)); }}
                            slotProps={{ textField: { size: "small", style: { width: 190 } } }}
                        />
                    </LocalizationProvider>
                    <IconButton size="small" onClick={() => shiftMonth(1)} aria-label="Next month"><ChevronRightIcon /></IconButton>
                </Box>

                <Box sx={{ flex: 1 }} />

                <Button
                    variant="outlined"
                    size="small"
                    startIcon={pdfAction === "open" ? <CircularProgress size={16} /> : <PictureAsPdfOutlinedIcon />}
                    disabled={!chart?.medications?.length || !!pdfAction}
                    onClick={() => openPdf("open")}
                >
                    View PDF
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={pdfAction === "download" ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon />}
                    disabled={!chart?.medications?.length || !!pdfAction}
                    onClick={() => openPdf("download")}
                >
                    Download
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            {/* Header band, matching the other reports' toolbar */}
            <Box
                sx={{
                    p: 2, mb: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center",
                    background: "linear-gradient(270.01deg, #026999 36.65%, #FFFFFF 103.85%)", borderRadius: 1,
                }}
            >
                <Box>
                    <h3 style={{ margin: 0 }}>{props.user.company_name}</h3>
                    <h5 style={{ margin: 0 }}>eMAR — Monthly Medication Administration Record</h5>
                </Box>
                <Box sx={{ color: "#fff", textAlign: "right" }}>
                    <Typography style={{ fontWeight: 700 }}>{selectedCustomer?.cus_name || "No client selected"}</Typography>
                    <Typography variant="body2">{moment(month, MONTH_FMT).format("MMMM YYYY")}</Typography>
                </Box>
            </Box>

            {!selectedCustomer && (
                <Typography color="textSecondary" sx={{ mt: 3, textAlign: "center" }}>
                    Select a client to view their eMAR.
                </Typography>
            )}

            {selectedCustomer && isLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
            )}

            {selectedCustomer && !isLoading && chart && (
                <>
                    {/* Summary */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
                        <Tile
                            label="Adherence"
                            value={chart.summary.adherence === null ? null : `${chart.summary.adherence}%`}
                            color={adherenceColor(chart.summary.adherence)}
                        />
                        {SUMMARY_TILES.map(tile => (
                            <Tile key={tile.key} label={tile.label} value={chart.summary[tile.key]} color={tile.color} />
                        ))}
                    </Box>

                    {chart.medications.length === 0 ? (
                        <Typography color="textSecondary" sx={{ mt: 3, textAlign: "center" }}>
                            No scheduled medications for {chart.monthLabel}.
                        </Typography>
                    ) : (
                        <Box sx={{ overflow: "auto", maxHeight: "calc(100vh - 430px)", border: BORDER, borderRadius: 1, background: "#fff" }}>
                            <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "100%", minWidth: gridWidth, tableLayout: "fixed", fontSize: 13 }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...stickyName, zIndex: 4, top: 0, background: "#E9EFF3", textAlign: "left", padding: "6px 8px", borderBottom: BORDER }}>Medication</th>
                                        <th style={{ ...stickyTime, zIndex: 4, top: 0, background: "#E9EFF3", textAlign: "left", padding: "6px 8px", borderBottom: BORDER }}>Time</th>
                                        {days.map(day => (
                                            <th
                                                key={day}
                                                style={{
                                                    position: "sticky", top: 0, zIndex: 3, width: CELL, minWidth: CELL, padding: "4px 0", textAlign: "center",
                                                    background: day === todayDay ? "#CFE6F1" : "#E9EFF3", borderBottom: BORDER, fontWeight: 600,
                                                }}
                                            >
                                                <div>{day}</div>
                                                <div style={{ fontSize: 10, color: "#6b7378", fontWeight: 400 }}>
                                                    {moment(month, MONTH_FMT).date(day).format("dd").charAt(0)}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {chart.medications.map(medication => {
                                        const s = medication.summary;
                                        const elapsed = s.administered + s.missed + s.notGiven;

                                        return (
                                            <React.Fragment key={medication.medicationID}>
                                                <tr>
                                                    <td colSpan={2} style={{ ...stickyName, width: NAME_COL + TIME_COL, maxWidth: NAME_COL + TIME_COL, background: "#EEF5F9", padding: "6px 8px", borderBottom: BORDER, fontWeight: 700 }}>
                                                        {medication.name}{medication.dosage ? ` — ${medication.dosage}` : ""}
                                                        {medication.discontinuedOn && (
                                                            <span style={{ color: EMAR_STATUS.notGiven.color, fontWeight: 600, marginLeft: 6 }}>
                                                                (stopped {moment(medication.discontinuedOn, "YYYY-MM-DD HH:mm:ss").format("MM/DD")})
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td colSpan={days.length} style={{ background: "#EEF5F9", padding: "6px 8px", borderBottom: BORDER, color: "#6b7378", fontSize: 12 }}>
                                                        {elapsed > 0 && `${s.administered}/${elapsed} given`}
                                                        {s.late > 0 && ` · ${s.late} late`}
                                                        {s.notGiven > 0 && ` · ${s.notGiven} not given`}
                                                        {s.missed > 0 && ` · ${s.missed} missed`}
                                                        {s.upcoming > 0 && ` · ${s.upcoming} upcoming`}
                                                    </td>
                                                </tr>
                                                {medication.rows.map(row => (
                                                    <tr key={row.time}>
                                                        <td style={{ ...stickyName, padding: "4px 8px", borderBottom: BORDER, color: "#6b7378", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={medication.name}>
                                                            {medication.name}
                                                        </td>
                                                        <td style={{ ...stickyTime, padding: "4px 8px", borderBottom: BORDER, fontWeight: 600, whiteSpace: "nowrap" }}>
                                                            {row.displayTime}
                                                        </td>
                                                        {row.cells.map(cell => {
                                                            const clickable = cell.status !== "notDue";
                                                            return (
                                                                <td
                                                                    key={cell.day}
                                                                    onClick={clickable ? () => setDetail({ medication, row, cell }) : undefined}
                                                                    title={clickable ? EMAR_STATUS[cell.status].label : undefined}
                                                                    style={{
                                                                        textAlign: "center", padding: "3px 0", borderBottom: BORDER, borderLeft: "1px solid #f0f3f5",
                                                                        cursor: clickable ? "pointer" : "default",
                                                                        background: cell.day === todayDay ? "#F2F8FB" : undefined,
                                                                    }}
                                                                >
                                                                    <StatusMark status={cell.status} />
                                                                </td>
                                                            );
                                                        })}
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </Box>
                    )}

                    {/* Legend */}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mt: 1.5 }}>
                        {EMAR_LEGEND_ORDER.filter(status => status !== "notDue").map(status => (
                            <Box key={status} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                <StatusMark status={status} size={16} />
                                <Typography variant="caption">{EMAR_STATUS[status].label}</Typography>
                            </Box>
                        ))}
                        <Typography variant="caption" color="textSecondary">
                            On time = within {chart.graceMinutes} min of the scheduled time.
                        </Typography>
                    </Box>

                    {chart.unscheduled.length > 0 && (
                        <Typography variant="caption" color="textSecondary" component="div" sx={{ mt: 1 }}>
                            Not on a schedule: {chart.unscheduled.map(m => (m.dosage ? `${m.name} (${m.dosage})` : m.name)).join(", ")}
                        </Typography>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, color: "#6b7378", fontSize: 12 }}>
                        <span>Generated {moment(chart.generatedAt, "YYYY-MM-DD HH:mm:ss").format("MM/DD/YYYY h:mm A")} using</span>
                        <FieldWorkerSVG />
                    </Box>
                </>
            )}

            <CellDetailDialog detail={detail} onClose={() => setDetail(null)} />
        </Box>
    );
}
